import { useBrowserAlertDelivery } from './useBrowserAlertDelivery.js'
import {useAlertFeedback} from './useAlertFeedback.jsx'
import { createContext,useContext,useEffect,useRef,useState } from 'react'
import { useAuth } from '../../hooks/useAuth.jsx'
import { useNotifications } from '../../hooks/useNotifications.jsx'
import { readAlertPresentation,persistAlertPresentation } from '../../lib/priceAlerts/presentation.js'
import { streamAlerts,alertCommand,alertNotification } from '../../lib/priceAlerts/client.js'

const EMPTY={alerts:[],status:'unavailable',error:'Alert monitoring unavailable',selectedId:null}
export const PriceAlertContext=createContext(null)
const Context=PriceAlertContext
export const usePriceAlerts=()=>useContext(Context)
export default function PriceAlertProvider({children, enabled = import.meta.env.VITE_EREVIS_PRICE_ALERTS_ENABLED === '1'}) {
  const owner=useAuth()?.user?.id
  const {upsertNotification,notifications=[],markNotificationRead}=useNotifications()
  const browserDelivery=useBrowserAlertDelivery(owner,notifications)
  const [receiptState,setReceiptState]=useState({owner:null,events:[]})
  const [seenState,setSeenState]=useState({owner:null,ids:{}})
  const [state,setState]=useState({...EMPTY,owner})
  const [selectedId,setSelectedId]=useState(null)
  const currentOwner=useRef(owner);currentOwner.current=owner
  const mutationControllers=useRef(new Set())
  useEffect(()=>{
    setSelectedId(null)
    setReceiptState({owner,events:[]})
    setSeenState({owner,ids:{}})
    if (!owner || !enabled) {setState({...EMPTY,owner,error:enabled?'Sign in to use alerts':'Price alert monitoring is not configured in this environment'});return}
    let active=true,timer,deadline,controller,failures=0,cursor=null,stateCursor=0,receipts=[]
    const key=`erevis:price-alerts:v1:${import.meta.env.VITE_EREVIS_PRODUCT || 'personal'}:${owner}`
    try {
      const saved=JSON.parse(localStorage.getItem(key)||'null')
      if (Number.isSafeInteger(saved?.cursor) && Array.isArray(saved.receipts)) {
        cursor=saved.cursor;receipts=saved.receipts
        receipts=receipts.slice(-50)
        receipts.forEach(e=>upsertNotification(alertNotification(e,owner)))
        setReceiptState({owner,events:receipts})
      }
    } catch {cursor=null;receipts=[]}
    const persist=()=>{try {localStorage.setItem(key,JSON.stringify({cursor,receipts}))} catch { /* Server retains receipts; reconnect remains possible. */ }}
    const deliver=event=>{
      receipts=[...receipts.filter(e=>e.id!==event.id),event].sort((a,b)=>a.createdAt-b.createdAt || String(a.id).localeCompare(String(b.id))).slice(-50)
      upsertNotification(alertNotification(event,owner))
    }
    async function connect() {
      controller=new AbortController()
      deadline=setTimeout(()=>controller.abort(),35000)
      setState(s=>({...s,owner,status:s.owner===owner&&s.status==='live'?'live':s.owner===owner&&s.alerts.length?'reconnecting':'connecting',error:null,alerts:s.owner===owner?s.alerts:[]}))
      try {
        await streamAlerts(owner,{after:cursor,signal:controller.signal,onFrame:(kind,data)=>{
          if (!active || currentOwner.current!==owner || controller.signal.aborted) return
          failures=0
          if (kind==='snapshot') {
            stateCursor=data.cursor
            data.receipts.slice().reverse().forEach(deliver)
            if (cursor===null) cursor=data.cursor
            else if (cursor>data.cursor) cursor=0
            setState({owner,alerts:data.alerts,status:'live',error:null})
          } else if (data.seq>cursor) {
            if (data.kind==='triggered') deliver(data.payload)
            else if(data.seq>stateCursor) setState(s=>{
              // Keep existing delete/edit targets in place during state updates.
              const alerts=data.kind==='deleted' ? s.alerts.filter(a=>a.id!==data.payload.id)
                : s.alerts.some(a=>a.id===data.payload.id) ? s.alerts.map(a=>a.id===data.payload.id?data.payload:a)
                : [data.payload,...s.alerts]
              return {...s,status:'live',alerts}
            })
            cursor=data.seq
          }
          setReceiptState({owner,events:[...receipts]})
          persist()
        }})
      } catch {
        if (active) setState(s=>({...s,status:'unavailable',error:'Alert updates unavailable. Saved alerts remain on the server.'}))
        failures++
      } finally {
        clearTimeout(deadline)
        if (active) timer=setTimeout(connect,failures?Math.min(30000,1000*2**Math.min(failures-1,5)):250)
      }
    }
    connect()
    return ()=>{active=false;clearTimeout(timer);clearTimeout(deadline);controller?.abort();for(const c of mutationControllers.current)c.abort();mutationControllers.current.clear()}
  },[owner,enabled,upsertNotification])
  async function command(body) {
    if (!owner || currentOwner.current!==owner) throw new Error('Sign in to use alerts')
    if (!enabled) throw new Error('Price alerts are not configured in this environment')
    const controller=new AbortController();mutationControllers.current.add(controller)
    try {
      const result=await alertCommand(owner,body,controller.signal)
      if (currentOwner.current!==owner) throw new Error('Account changed')
      // SSE owns the authoritative list. A command acknowledgement is not a
      // second state writer that could overwrite a newer stream revision.
      return result
    } finally {mutationControllers.current.delete(controller)}
  }
  const {submit,feedback}=useAlertFeedback(command,owner,setSelectedId)
  // Receipt history is independently bounded, so broker notifications cannot
  // evict recent price alerts. Read state is the existing owner-local UI state.
  const recent=(enabled && receiptState.owner===owner ? receiptState.events : [])
    .map(event=>{
      const receipt=alertNotification(event,owner)
      const current=notifications.find(n=>n.id===receipt.id) || receipt
      const local=readAlertPresentation(owner,receipt.id)
      return {...current,read:current.read===true || local.read===true || (seenState.owner===owner && seenState.ids[receipt.id]===true),cleared:current.cleared===true || local.cleared===true,dismissed:current.dismissed===true || local.dismissed===true}
    })
    .filter(receipt=>!receipt.cleared && !receipt.dismissed)
    .sort((a,b)=>String(b.firedAt||b.createdAt||'').localeCompare(String(a.firedAt||a.createdAt||'')))
  const recentUnreadCount=recent.filter(receipt=>!receipt.read).length
  function markReceiptRead(id) {
    const receipt=recent.find(item=>item.id===id)
    if(!receipt || receipt.read || currentOwner.current!==owner)return
    persistAlertPresentation(owner,[{...receipt,source:'price-alert',ownerUserId:owner,read:true}])
    markNotificationRead?.(id)
    setSeenState(current=>({owner,ids:{...(current.owner===owner?current.ids:{}),[id]:true}}))
  }
  // Commands use their own authenticated request and server revision checks.
  // Feed renewal must not disable them or imply that server monitoring stopped.
  const value={browserDelivery,canCommand:Boolean(owner && enabled),recent,recentUnreadCount,markReceiptRead,...(state.owner===owner?state:EMPTY),selectedId,select:setSelectedId,command,submit}
  return <Context.Provider value={value}>{children}{feedback}</Context.Provider>
}
